import { BlobPreview } from "@/components/BlobPreview";
import { useCaseFileBlobUrl } from "@/lib/protectedFile";

/** File extensions rendered as an `<img>` rather than in the frame. */
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "svg"];
/** File extensions the Office Online viewer handles. */
const OFFICE_EXTENSIONS = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];

/**
 * What the case-file preview shows inside its dialog.
 *
 * A file served by the backend proxy (`proxy_url`) sits behind the default-deny router, so a frame
 * pointed at it sends no bearer token and gets a 401. It is fetched through the API client and shown
 * from an object URL, with an explicit loading and error state in place of a blank frame. Kept apart
 * from the dialog so it carries no Radix component, which the pinned Jest resolver cannot mount.
 *
 * @param {{name?: string, url?: string, proxy_url?: string, preview_src?: string, preview_kind?: string}} props.file
 *   the row the user clicked: `preview_src` is a short-lived viewer URL on another host, `url` the durable link.
 */
export default function FilePreviewBody({ file }) {
  const name = file.name || "file";
  const ext = (name.split(".").pop() || "").toLowerCase();
  const isPdf = ext === "pdf";
  const isImage = IMAGE_EXTENSIONS.includes(ext);
  const isOffice = OFFICE_EXTENSIONS.includes(ext);

  // OneDrive feeds a separate short-lived CDN url for the iframe, which wins over the proxy.
  const viaProxy = !file.preview_src && !!file.proxy_url;
  const proxied = useCaseFileBlobUrl(viaProxy ? file.proxy_url : null);

  if (viaProxy) {
    return <BlobPreview state={proxied} name={name} testIdPrefix="customer-file-preview" />;
  }

  const previewUrl = file.preview_src || file.url;

  if (!previewUrl) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
        No URL on file. Open <strong className="mx-1">{file.source}</strong> directly to view it.
      </div>
    );
  }

  if (isImage) {
    return (
      <div className="h-full overflow-auto flex items-center justify-center p-4">
        <img src={previewUrl} alt={name} className="max-w-full max-h-full object-contain" />
      </div>
    );
  }

  let iframeSrc = previewUrl;
  // `preview_kind: "embedded"` means the URL is already a complete viewer
  // page (e.g. SharePoint `_layouts/15/embed.aspx`) — DON'T wrap it in
  // Office Online or append #view=FitH; both would break the embed.
  if (file.preview_kind !== "embedded") {
    if (isPdf) {
      iframeSrc = previewUrl + (previewUrl.includes("#") ? "" : "#view=FitH&toolbar=1");
    } else if (isOffice) {
      iframeSrc = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(previewUrl)}`;
    }
  }

  return (
    <iframe
      title={name}
      src={iframeSrc}
      className="w-full h-full"
      style={{ border: 0 }}
      data-testid="customer-file-preview-iframe"
    />
  );
}
